/**
 * @fileoverview Section "Motion" — durations, easings, démos hover / press.
 *
 * Le motion GNPC est **discret** : 100–400 ms max, easings standards, aucune
 * animation gratuite. Objectif = feedback d'interaction, pas décoration.
 * `prefers-reduced-motion: reduce` coupe toute animation ≥ 20 ms (défini
 * dans `globals.css`).
 */

import { CopyButton } from '@gnpc/ui';
import { DsPreview } from '../components/DsPreview.tsx';

const DURATIONS = [
  {
    token: '--duration-fast',
    ms: 100,
    class: 'duration-100',
    usage: 'Hover simple (bouton, lien) — feedback quasi instantané.',
  },
  {
    token: '--duration-normal',
    ms: 200,
    class: 'duration-200',
    usage: 'Défaut — apparition dropdown, popover, tooltip, changement de state.',
  },
  {
    token: '--duration-slow',
    ms: 400,
    class: 'duration-400',
    usage: 'Réservé aux transitions majeures (dialog, drawer, page).',
  },
] as const;

const EASINGS = [
  { class: 'ease-linear', usage: 'Rare — progression déterministe (loader).' },
  { class: 'ease-in-out', usage: 'Défaut apparition / disparition.' },
  { class: 'ease-out', usage: 'Entrée d\'un élément (dialog, tooltip).' },
  { class: 'ease-in', usage: 'Sortie d\'un élément.' },
] as const;

export function DsSectionMotion(): JSX.Element {
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">Durations</h3>
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Token CSS</th>
                <th className="px-4 py-2 text-left font-medium">Valeur</th>
                <th className="px-4 py-2 text-left font-medium">Classe Tailwind</th>
                <th className="px-4 py-2 text-left font-medium">Usage</th>
                <th className="px-4 py-2 text-left font-medium">Démo hover</th>
              </tr>
            </thead>
            <tbody>
              {DURATIONS.map((d) => (
                <tr key={d.token} className="border-t border-border">
                  <td className="px-4 py-3 font-mono text-xs">{d.token}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground tabular-nums">
                    {d.ms} ms
                  </td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1">
                      <code className="font-mono text-xs">{d.class}</code>
                      <CopyButton value={d.class} ariaLabel={`Copier ${d.class}`} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{d.usage}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      /* `bg-primary` (pas orange-500) : bouton interactif à
                       * texte blanc → contraste WCAG AA 4,5:1 requis. */
                      className={`inline-flex h-8 items-center rounded-md bg-primary px-3 text-xs font-medium text-white transition-transform hover:scale-110 ${d.class}`}
                    >
                      Hover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">Easings</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {EASINGS.map((e) => (
            <DsPreview key={e.class} label={e.class} align="center">
              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  className={`inline-flex h-8 items-center rounded-md bg-quipux-navy-950 px-3 text-xs font-medium text-white transition-transform duration-500 hover:translate-x-8 ${e.class}`}
                >
                  Survoler
                </button>
                <p className="text-center text-[10px] text-muted-foreground">{e.usage}</p>
              </div>
            </DsPreview>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-quipux-blue-200 bg-quipux-blue-50 p-4 text-xs text-quipux-navy-900">
        <p className="mb-1 font-semibold">Accessibilité</p>
        <p>
          Tout est capé à 0,01 ms si l'utilisateur a activé <code>prefers-reduced-motion:
          reduce</code>. Aucune règle motion à écrire en dehors de <code>globals.css</code>{' '}
          qui applique ce reset.
        </p>
      </div>
    </div>
  );
}
